"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@wtc/ui";
import type { BrandSummary } from "@/lib/marketing/brands";

/**
 * The brand list, for client surfaces.
 *
 * WHY A HOOK AT ALL: brands used to be a compile-time union that a component
 * could import, so every picker and filter was built from a module constant.
 * They are database rows now — a brand onboarded from Marketing → Brands has to
 * appear in the composer, the filters and the asset library without a deploy —
 * so the list comes over the wire from `/api/marketing/brands`.
 *
 * The payload is `BrandSummary`: display name, voice, and which platforms the
 * brand can publish to. Never a credential, never a webhook (the API cannot send
 * them; see lib/marketing/brand-registry.ts).
 */
export interface UseMarketingBrandsResult {
  readonly brands: readonly BrandSummary[];
  /** `{label, value}` pairs for a select / filter config. */
  readonly options: ReadonlyArray<{ label: string; value: string }>;
  readonly loading: boolean;
  readonly error: string | null;
  /** True when `id` is a registered, active brand. */
  readonly isKnownBrand: (id: string) => boolean;
  readonly byId: (id: string) => BrandSummary | undefined;
  readonly refresh: () => Promise<void>;
}

export function useMarketingBrands(options?: {
  readonly includeArchived?: boolean;
}): UseMarketingBrandsResult {
  const includeArchived = options?.includeArchived ?? false;
  const { getAuthToken } = useAuth();
  const [brands, setBrands] = useState<BrandSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const res = await fetch(
        `/api/marketing/brands${includeArchived ? "?archived=1" : ""}`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      if (!res.ok) throw new Error("Could not load the brand list.");
      const data = (await res.json()) as { brands?: BrandSummary[] };
      setBrands(data.brands ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the brand list.");
      // Deliberately NOT cleared: a transient failure should leave whatever list
      // is already on screen rather than emptying a picker mid-edit.
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, includeArchived]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const optionList = useMemo(
    () => brands.map((brand) => ({ label: brand.displayName, value: brand.id })),
    [brands],
  );

  const isKnownBrand = useCallback(
    (id: string) => brands.some((brand) => brand.id === id),
    [brands],
  );

  const byId = useCallback(
    (id: string) => brands.find((brand) => brand.id === id),
    [brands],
  );

  return {
    brands,
    options: optionList,
    loading,
    error,
    isKnownBrand,
    byId,
    refresh,
  };
}
